import apiClient from '@/api/client'
import type { PaginatedResponse } from '@/api/services/base.service'
import type {
  KbAssistantProposal,
  KbAssistantProposalAcceptPayload,
  KbAssistantProposalAcceptResult,
  KbAssistantProposalCounts,
  KbAssistantProposalExportResponse,
  KbAssistantProposalListParams,
  KbAssistantProposalStatsQuery,
  KbAssistantProposalStatsResponse,
  KbAssistantProposalUpdatePayload,
} from '@/types/kbAssistantProposal.types'

/**
 * HTTP client for the KB Assistant review queue (`/api/v1/kb-assistant-proposal`,
 * contract KA1–KA7).
 *
 * Deliberately **not** extending `BaseApiService`: that base appends a trailing
 * slash to the collection URL, while these routes are registered without one, so
 * every list/create would eat a 307 redirect.
 *
 * There is no `create` here. Proposals are authored by the external KB Assistant
 * agent with an API key; the admin UI only ever reads and adjudicates them. A
 * create method would be dead code inviting misuse.
 *
 * There is no `delete` either — rows are the assistant's track record.
 */
class KbAssistantProposalService {
  private readonly endpoint = '/kb-assistant-proposal'

  /**
   * KA2 — the queue. Newest-first, `status=pending` by default.
   *
   * Array params are serialized as repeated keys (`?kind=a&kind=b`), which is
   * what FastAPI's `list[str] = Query(...)` expects; axios' default bracket
   * notation (`kind[]=a`) would silently drop them.
   */
  async list(
    params: KbAssistantProposalListParams = {},
  ): Promise<PaginatedResponse<KbAssistantProposal>> {
    const { data } = await apiClient.get<PaginatedResponse<KbAssistantProposal>>(this.endpoint, {
      params,
      paramsSerializer: { indexes: null },
    })
    return data
  }

  /**
   * KA3 — every badge in one call.
   *
   * Omit `topicId` for the sidebar total: the param scopes the **whole**
   * response, not just the per-topic map.
   */
  async counts(topicId?: string): Promise<KbAssistantProposalCounts> {
    const { data } = await apiClient.get<KbAssistantProposalCounts>(`${this.endpoint}/counts`, {
      params: topicId ? { topic_id: topicId } : undefined,
    })
    return data
  }

  /**
   * KS1 — reviewer-reaction counts (`api-contract-kb-assistant-stats.md`).
   *
   * `from` / `to` must be offset-aware ISO-8601 (a naive value is a `422`);
   * omit either for an open-ended period.
   */
  async getStats(
    query: KbAssistantProposalStatsQuery = {},
  ): Promise<KbAssistantProposalStatsResponse> {
    const { data } = await apiClient.get<KbAssistantProposalStatsResponse>(
      `${this.endpoint}/stats`,
      { params: query },
    )
    return data
  }

  /**
   * KS2 — one record per proposal of the period, for offline analysis. Admin
   * session only; same `from` / `to` as KS1. A period over the cap is a `422`
   * with code `export-too-large`.
   */
  async exportStats(
    query: KbAssistantProposalStatsQuery = {},
  ): Promise<KbAssistantProposalExportResponse> {
    const { data } = await apiClient.get<KbAssistantProposalExportResponse>(
      `${this.endpoint}/stats/export`,
      { params: query },
    )
    return data
  }

  /** KA4 — the review screen's payload: proposal + full diff + full case list. */
  async getById(id: string): Promise<KbAssistantProposal> {
    const { data } = await apiClient.get<KbAssistantProposal>(`${this.endpoint}/${id}`)
    return data
  }

  /**
   * KA5 — "Modifica". Writes only `final_*`; the proposal stays `pending`.
   *
   * Send an explicit `null` to clear a facet (reverting it to the assistant's
   * original); omit the key to leave it untouched. Do not strip nulls before
   * calling — that turns a clear into a no-op.
   */
  async update(
    id: string,
    payload: KbAssistantProposalUpdatePayload,
  ): Promise<KbAssistantProposal> {
    const { data } = await apiClient.patch<KbAssistantProposal>(`${this.endpoint}/${id}`, payload)
    return data
  }

  /**
   * KA6 — accept. Mints or updates a `draft` version and nothing downstream of
   * it; publishing stays a separate, explicit action.
   *
   * Pass `acknowledge_stale: true` only after the user has seen the staleness
   * warning — without it a stale proposal returns `409 proposal-stale`.
   */
  async accept(
    id: string,
    payload: KbAssistantProposalAcceptPayload = {},
  ): Promise<KbAssistantProposalAcceptResult> {
    const { data } = await apiClient.post<KbAssistantProposalAcceptResult>(
      `${this.endpoint}/${id}/accept`,
      payload,
    )
    return data
  }

  /** KA7 — terminal reject. `reason` is also the training signal for the assistant. */
  async reject(id: string, reason?: string | null): Promise<KbAssistantProposal> {
    const { data } = await apiClient.post<KbAssistantProposal>(`${this.endpoint}/${id}/reject`, {
      reason: reason ?? null,
    })
    return data
  }
}

/**
 * Pull the server's error envelope out of an axios failure.
 *
 * Without this the UI shows axios' own `"Request failed with status code 409"`,
 * which tells the operator nothing — and every meaningful failure on this
 * surface is a 409 with a *different* remedy: `lock-conflict` (someone else is
 * editing), `proposal-stale` (the paragraph moved), `proposal-not-pending`
 * (already reviewed), `title-collision` (rename it first). The backend already
 * sends `{ error: { code, message, details } }`; this just surfaces it.
 *
 * Returns `[code, message]` — the code so the caller can branch, the message so
 * it has something to show when it does not.
 */
export function extractApiError(err: unknown): [string | null, string] {
  const envelope = (
    err as {
      response?: {
        data?: { error?: { code?: string; message?: string; details?: { reason?: string } } }
      }
    }
  )?.response?.data?.error
  if (envelope?.message) {
    // `code` alone is not always enough to pick a remedy. `conflict` is the
    // generic 409 and hides at least four different situations — a topic title
    // that already exists (rename it), a per-parent cap, an edited proposal
    // being superseded, a target that moved — so when the server names a
    // `details.reason`, that is the more specific key. Returned as
    // `code:reason` so a caller can look up either shape.
    const reason = envelope.details?.reason
    const code = envelope.code ?? null
    return [reason && code ? `${code}:${reason}` : (code ?? reason ?? null), envelope.message]
  }
  if (err instanceof Error) return [null, err.message]
  return [null, String(err)]
}

export const kbAssistantProposalService = new KbAssistantProposalService()
export default kbAssistantProposalService
